import { useMemo, useRef, useState } from 'react'
import { useSaveSearchQuery } from '@/hooks/saved-search/useSaveSearchQuery'
import { opportunitySavedSearch } from '@/lib/saved-search'
import type { OpportunityListItem } from '../../-hooks/useOpportunities'
import { CustomSearchCaptain } from '../../-lib/custom-search-captain'

// 検索状態の型
export type CustomSearchState = {
  query: string
  exclude: string
  tags: Array<{ id?: number; group: string | null; name: string }>
  excludeTags: Array<{ id?: number; group: string | null; name: string }>
  customerId: number | null
  verticalDivisionId: number | null
  lateralDivisionId: number | null
  createdBy: number | null
  salesRecordingGroupId: number | null
  opportunityStatusIds: number[]
  incentiveFilter: 'target' | 'disabled' | null
  opportunityTypeId: number | null
}

// 空の検索状態
const emptySearchState: CustomSearchState = {
  query: '',
  exclude: '',
  tags: [],
  excludeTags: [],
  customerId: null,
  verticalDivisionId: null,
  lateralDivisionId: null,
  createdBy: null,
  salesRecordingGroupId: null,
  opportunityStatusIds: [],
  incentiveFilter: null,
  opportunityTypeId: null,
}

type UseCustomSearchDialogParams = {
  open: boolean
  opportunities: OpportunityListItem[]
  onOpenChange: (open: boolean) => void
  onSearch: (searchText: string) => void
  currentSearchText?: string
}

export function useCustomSearchDialog({
  open,
  opportunities,
  onOpenChange,
  onSearch,
  currentSearchText,
}: UseCustomSearchDialogParams) {
  const [step, setStep] = useState<1 | 2>(1)
  const [queryName, setQueryName] = useState('')
  const [searchState, setSearchState] = useState<CustomSearchState>(emptySearchState)

  const saveSearchQuery = useSaveSearchQuery(opportunitySavedSearch)

  // 前回のopen状態を保持
  const prevOpenRef = useRef(open)

  // 検索状態の初期値を計算
  const getInitialSearchState = (): CustomSearchState => {
    if (currentSearchText) {
      const parser = new CustomSearchCaptain()
      parser.parse(currentSearchText)
      return {
        query: parser.query,
        exclude: parser.exclude,
        tags: parser.tags,
        excludeTags: parser.excludeTags,
        customerId: parser.customerId,
        verticalDivisionId: parser.verticalDivisionId,
        lateralDivisionId: parser.lateralDivisionId,
        createdBy: parser.createdBy,
        salesRecordingGroupId: parser.salesRecordingGroupId,
        opportunityStatusIds: parser.opportunityStatusIds,
        incentiveFilter: parser.incentiveFilter,
        opportunityTypeId: parser.opportunityTypeId,
      }
    }
    return emptySearchState
  }

  // openがfalse→trueに変化したときに検索条件を反映（レンダリング中に実行）
  if (open && !prevOpenRef.current) {
    const initialState = getInitialSearchState()
    // 状態が異なる場合のみ更新（無限ループ防止）
    if (JSON.stringify(searchState) !== JSON.stringify(initialState)) {
      setSearchState(initialState)
      setStep(1)
    }
    // step1 のキャンセルは閉じるときのリセット（handleOpenChange）を経由しないため、
    // 前回 step2 で入力した検索条件名を開いたときに破棄する
    setQueryName('')
  }
  prevOpenRef.current = open

  // オポチュニティデータから抽出したユニークなリスト
  const captains = useMemo(() => {
    type CaptainItem = { id: number; employeeNumber: number | null; name: string; text: string }
    const uniqueItemsMap = opportunities.reduce<Record<number, CaptainItem>>((acc, c) => {
      if (c.created_by && !acc[c.created_by]) {
        acc[c.created_by] = {
          id: c.created_by,
          employeeNumber: c.created_by_employee_number,
          name: c.created_by_name,
          text: `${c.created_by_employee_number || '不明'} ${c.created_by_name}`,
        }
      }
      return acc
    }, {})
    return Object.values(uniqueItemsMap).sort((a, b) => a.id - b.id)
  }, [opportunities])

  const customers = useMemo(() => {
    type CustomerItem = { id: number; name: string | null }
    const uniqueItemsMap = opportunities.reduce<Record<number, CustomerItem>>((acc, c) => {
      if (c.customer_id && !acc[c.customer_id]) {
        acc[c.customer_id] = {
          id: c.customer_id,
          name: c.customer_name,
        }
      }
      return acc
    }, {})
    return Object.values(uniqueItemsMap)
      .filter((c) => c.name)
      .sort((a, b) => a.id - b.id)
  }, [opportunities])

  const verticalDivisions = useMemo(() => {
    type DivisionItem = { id: number; name: string; shortName: string }

    const uniqueItemsMap = opportunities.reduce<Record<number, DivisionItem>>((acc, c) => {
      if (c.vertical_division_id && !acc[c.vertical_division_id]) {
        const shortName = c.short_vertical_division_name || c.vertical_division_name || ''
        const fullName = c.vertical_division_name || ''

        acc[c.vertical_division_id] = {
          id: c.vertical_division_id,
          name: fullName.split('#').join(' '),
          shortName,
        }
      }
      return acc
    }, {})
    return Object.values(uniqueItemsMap).sort((a, b) => a.id - b.id)
  }, [opportunities])

  const lateralDivisions = useMemo(() => {
    type DivisionItem = { id: number; name: string; shortName: string }

    const uniqueItemsMap = opportunities.reduce<Record<number, DivisionItem>>((acc, c) => {
      if (c.lateral_division_id && !acc[c.lateral_division_id]) {
        const shortName = c.short_lateral_division_name || c.lateral_division_name || ''
        const fullName = c.lateral_division_name || ''

        acc[c.lateral_division_id] = {
          id: c.lateral_division_id,
          name: fullName.split('#').join(' '),
          shortName,
        }
      }
      return acc
    }, {})
    return Object.values(uniqueItemsMap).sort((a, b) => a.id - b.id)
  }, [opportunities])

  const salesRecordingGroups = useMemo(() => {
    type GroupItem = { id: number; name: string }
    const uniqueItemsMap = opportunities.reduce<Record<number, GroupItem>>((acc, c) => {
      if (c.sales_recording_group_id && !acc[c.sales_recording_group_id] && c.sales_recording_group_name) {
        acc[c.sales_recording_group_id] = {
          id: c.sales_recording_group_id,
          name: c.sales_recording_group_name,
        }
      }
      return acc
    }, {})
    return Object.values(uniqueItemsMap).sort((a, b) => a.id - b.id)
  }, [opportunities])

  // 検索条件が有効かチェック
  const isSearchEnabled = useMemo(() => {
    const parser = new CustomSearchCaptain(searchState)
    return parser.enabled()
  }, [searchState])

  // ダイアログの開閉を制御
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // ダイアログを閉じるときにステップと条件名をリセット
      setStep(1)
      setQueryName('')
    }
    onOpenChange(newOpen)
  }

  // 検索実行
  const handleSearch = () => {
    const parser = new CustomSearchCaptain(searchState)
    const queryString = parser.stringify()
    onSearch(queryString)
    handleOpenChange(false)
  }

  // 検索条件を保存
  const handleSaveQuery = () => {
    if (!queryName) return

    const parser = new CustomSearchCaptain(searchState)
    const queryString = parser.stringify()

    // 新しいクエリを保存
    saveSearchQuery.mutate({
      name: queryName,
      query: queryString,
      createdAt: Date.now(),
    })

    // 検索実行
    handleSearch()
  }

  // ステータスチェックボックスのトグル
  const handleStatusToggle = (statusId: number, checked: boolean) => {
    setSearchState((prev) => ({
      ...prev,
      opportunityStatusIds: checked
        ? [...prev.opportunityStatusIds, statusId]
        : prev.opportunityStatusIds.filter((id) => id !== statusId),
    }))
  }

  return {
    // 状態
    step,
    queryName,
    searchState,
    isSearchEnabled,

    // セレクトオプション
    captains,
    customers,
    verticalDivisions,
    lateralDivisions,
    salesRecordingGroups,

    // ハンドラ
    setStep,
    setQueryName,
    setSearchState,
    handleOpenChange,
    handleSearch,
    handleSaveQuery,
    handleStatusToggle,
  }
}
